class InfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.productsContainerSelector = this.getAttribute('data-products-container');
    this.paginationSelector = this.getAttribute('data-pagination-selector');

    this.productsContainerEl = this.querySelector(this.productsContainerSelector);
    this.loadingIndicator = this.querySelector('.loading-overlay-wrapper');
    this.paginationWrapper = this.querySelector('.pagination-wrapper');
    this.paginationEl = this.paginationWrapper
      ? this.paginationWrapper.querySelector(this.paginationSelector)
      : this.querySelector(this.paginationSelector);

    if (!this.productsContainerEl || !this.paginationEl) {
      console.warn('Infinite Scroll: Missing products container or pagination element.');
      return;
    }

    // Force block layout so children have correct document positions
    this.style.display = 'block';

    // Read the next-page URL directly from the pagination.
    // On the Dawn theme the "Next page" arrow has aria-label="Next page" but
    // confusingly uses the class "pagination__item--next" on the PREVIOUS arrow
    // on the last page. We therefore look for the link whose aria-label is
    // exactly "Next page" AND which has a non-null href (i.e. it is not the
    // current page placeholder).
    this.nextUrl = this._getNextUrl(this.paginationEl);

    // Nothing to do if the whole collection fits on one page
    if (!this.nextUrl) return;

    // Hide the pagination nav — it is only used internally to track URLs
    this.paginationEl.setAttribute('aria-hidden', 'true');
    this.paginationEl.style.cssText =
      'visibility: hidden; pointer-events: none; height: 1px; overflow: hidden; margin: 0; padding: 0;';

    this._loading = false;

    this.observer = new IntersectionObserver(
      this._onIntersect.bind(this),
      { rootMargin: '0px 0px 400px 0px' }
    );

    this.observer.observe(this.paginationWrapper || this.paginationEl);
  }

  // Find the "Next page" link by looking for an <a> with a real href whose
  // aria-label is "Next page". This is robust against Dawn's confusing class
  // naming where "pagination__item--next" appears on the Previous arrow on
  // the last page.
  _getNextUrl(paginationEl) {
    if (!paginationEl) return null;
    var links = paginationEl.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) {
      if (links[i].getAttribute('aria-label') === 'Next page') {
        return links[i].getAttribute('href');
      }
    }
    return null;
  }

  _onIntersect(entries) {
    if (entries[0].isIntersecting && !this._loading && this.nextUrl) {
      this._loadNextPage();
    }
  }

  _loadNextPage() {
    this._loading = true;
    if (this.loadingIndicator) this.loadingIndicator.style.display = 'block';

    // Update the browser address bar (SEO + Back-button support)
    if (history.pushState) {
      history.pushState({ path: this.nextUrl }, '', this.nextUrl);
    }

    fetch(this.nextUrl)
      .then(function(res) {
        if (!res.ok) throw new Error(res.statusText);
        return res.text();
      })
      .then(this._handleNextPage.bind(this))
      .catch(function(err) {
        console.error('Infinite Scroll: fetch error', err);
      })
      .finally(function() {
        this._loading = false;
        if (this.loadingIndicator) this.loadingIndicator.style.display = 'none';
      }.bind(this));
  }

  _handleNextPage(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');

    // 1. Append new product items to the existing grid
    var newItems = doc.querySelectorAll(this.productsContainerSelector + ' .grid__item');
    newItems.forEach(function(item) {
      this.productsContainerEl.appendChild(item);
    }.bind(this));

    // 2. Check whether the fetched page has a "Next page" link.
    //    We use the same _getNextUrl helper so the logic is consistent.
    var newPaginationEl = doc.querySelector(this.paginationSelector);
    var newNextUrl = this._getNextUrl(newPaginationEl);

    if (newNextUrl) {
      // More pages remain — store the URL for the next intersection trigger
      this.nextUrl = newNextUrl;
    } else {
      // This was the last page — stop everything and remove the trigger element
      // so the page ends cleanly at the last product row with no loop
      this.nextUrl = null;
      this.observer.disconnect();
      if (this.paginationWrapper && this.paginationWrapper.parentNode) {
        this.paginationWrapper.parentNode.removeChild(this.paginationWrapper);
      }
    }
  }
}

customElements.define('infinite-scroll', InfiniteScroll);